import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LogOut, ShoppingBag, DollarSign, Package, PawPrint } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TOKEN_KEY = "fetchly_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadData = async (t = token) => {
    try {
      const [oRes, sRes] = await Promise.all([
        api.get("/admin/orders", { headers: { Authorization: `Bearer ${t}` } }),
        api.get("/admin/stats", { headers: { Authorization: `Bearer ${t}` } }),
      ]);
      setOrders(oRes.data);
      setStats(sRes.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        setToken("");
        localStorage.removeItem(TOKEN_KEY);
        toast.error("Session expired — please log in again.");
      }
    }
  };

  useEffect(() => {
    if (token) loadData();
    // eslint-disable-next-line
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", form);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      await loadData(data.token);
      toast.success("Welcome back!");
    } catch {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await api.post("/admin/logout", {}, { headers: { Authorization: `Bearer ${token}` } });
      }
    } catch { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setOrders([]);
    setStats(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="rounded-3xl border-border max-w-md w-full p-10" data-testid="admin-login-card">
          <div className="flex items-center gap-3 mb-6">
            <img
              src="https://customer-assets.emergentagent.com/job_fetch-toy-store/artifacts/xq3326bd_Gemini_Generated_Image_sijaotsijaotsija.png"
              alt="Pawventure Essentials"
              className="w-11 h-11 rounded-full object-cover ring-1 ring-border"
            />
            <div>
              <h1 className="font-display text-2xl font-bold leading-none">Pawventure Admin</h1>
              <p className="text-xs text-muted-foreground mt-1">Sign in to view orders</p>
            </div>
          </div>
          <form className="space-y-4" onSubmit={login}>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="admin-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="admin@petball.com"
                className="rounded-full h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="admin-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="rounded-full h-11"
              />
            </div>
            <Button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full font-bold"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_fetch-toy-store/artifacts/xq3326bd_Gemini_Generated_Image_sijaotsijaotsija.png"
              alt="Pawventure Essentials"
              className="w-10 h-10 rounded-full object-cover ring-1 ring-border"
            />
            <div>
              <h1 className="font-display font-bold text-lg leading-none">Admin Dashboard</h1>
              <p className="text-xs text-muted-foreground mt-0.5">Pawventure Essentials orders</p>
            </div>
          </div>
          <Button data-testid="admin-logout" onClick={logout} variant="outline" className="rounded-full">
            <LogOut size={14} className="mr-2" strokeWidth={2.5} /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="admin-stats">
          {[
            { icon: ShoppingBag, label: "Total orders", value: stats?.total_orders ?? 0, color: "text-primary" },
            { icon: DollarSign, label: "Total revenue", value: `$${(stats?.total_revenue ?? 0).toFixed(2)}`, color: "text-secondary" },
            { icon: Package, label: "Units sold", value: stats?.total_units ?? 0, color: "text-[hsl(var(--accent-foreground))]" },
          ].map(({ icon: Icon, label, value, color }) => (
            <Card key={label} className="rounded-3xl border-border p-6">
              <Icon className={color} size={22} strokeWidth={2.5} />
              <div className="mt-4 text-3xl font-display font-bold">{value}</div>
              <div className="text-sm text-muted-foreground mt-1">{label}</div>
            </Card>
          ))}
        </div>

        {/* Orders */}
        <Card className="rounded-3xl border-border overflow-hidden" data-testid="orders-table-card">
          <div className="px-6 py-5 border-b border-border">
            <h2 className="font-display font-bold text-xl">Recent orders</h2>
            <p className="text-sm text-muted-foreground mt-1">{orders.length} order(s) — newest first</p>
          </div>
          {orders.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground" data-testid="empty-orders">
              No orders yet — once a customer checks out, they'll show up here.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Ship to</TableHead>
                  <TableHead>Shipping</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                    <TableCell className="text-sm whitespace-nowrap">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="font-semibold">{o.name}</div>
                      <div className="text-xs text-muted-foreground">{o.email}</div>
                      {o.phone && <div className="text-xs text-muted-foreground">{o.phone}</div>}
                    </TableCell>
                    <TableCell className="text-sm capitalize">{o.color} / {o.size}</TableCell>
                    <TableCell>{o.quantity}</TableCell>
                    <TableCell className="text-xs max-w-[220px]">
                      {o.shipping ? (
                        <div className="leading-tight">
                          <div>{o.shipping.line1}{o.shipping.line2 ? `, ${o.shipping.line2}` : ""}</div>
                          <div className="text-muted-foreground">
                            {[o.shipping.city, o.shipping.state, o.shipping.postal_code].filter(Boolean).join(", ")}
                            {o.shipping.country ? ` · ${o.shipping.country}` : ""}
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {o.shipping_amount != null ? (
                        <div className="leading-tight">
                          <div className="font-semibold">${o.shipping_amount.toFixed(2)}</div>
                          {o.shipping_rate_name && (
                            <div className="text-muted-foreground max-w-[160px]">{o.shipping_rate_name}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-semibold whitespace-nowrap">
                      ${((o.amount || 0) + (o.shipping_amount || 0)).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Badge className="rounded-full bg-primary/10 text-primary border-0 capitalize">
                        {o.payment_status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </main>
    </div>
  );
}
